import { NotesFilterOptions } from '@common/interfaces/notes'
import { IconOrSvg } from '../components/Common/Icon'

export const ACCEPT_TYPES = {
  excel: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ],
}

export const EXCEL_DATE_FORMAT = 'yyyy-mm-dd'

export const YEAR_FORMAT = 'yyyy'
export const YEAR_FORMAT_MOMENT = 'YYYY'
export const MONTH_YEAR_FORMAT_MOMENT = 'MMM YYYY'
export const MONTH_YEAR_FORMAT_SHORT_MOMENT = 'MMM YY'

export const QUARTER_YEAR_FORMAT_SHORT_MOMENT = '[Q]Q YY'

export const DATE_FORMAT = 'MM/dd/yy'

// Four-digit year, for date fields with no practical range — a two-digit year
// silently windows a typed date into 1930-2029.
export const DATE_FORMAT_FULL_YEAR = 'MM/dd/yyyy'

export const DATE_TIME_FORMAT = 'MM/dd/yy HH:mm'

// localStorage key for the Detailed/Condensed display toggle. One key per
// surface; the Income Statement and Balance Sheet deliberately share this one
// so their column widths stay aligned for the coupled horizontal scroll.
export const FINANCIALS_DENSITY_STORAGE_KEY = 'financials:density'

export const DATE_FORMAT_MOMENT = 'M/D/YY'

export const DATE_FORMAT_SHORT_MOMENT = 'M/YY'

export const DATE_FORMAT_FULL_YEAR_MOMENT = 'MM/DD/YYYY'

export const DATE_TIME_FORMAT_MOMENT = 'M/D/YY - h:mm a'

// Full, unambiguous local date + time — e.g. "Jul 14, 2026, 3:42 PM".
// Used for hover tooltips where the always-visible label is relative/short.
export const DATE_TIME_FORMAT_MOMENT_FULL = 'MMM D, YYYY, h:mm A'

export const TIME_FORMAT_MOMENT = 'h:mm a'

export const DATE_FORMAT_MOMENT_FULL = 'MMM D, YYYY'

export const DATE_FORMAT_DOT = 'D.MMM.YYYY'

export const DATE_DATABASE_FORMAT = 'YYYY-MM-DD'

// Atlas has no per-user timezone anywhere in its model, so wherever a day
// boundary has to be pinned it is pinned here — matching the server's own clock.
export const EASTERN_TIME_ZONE = 'America/New_York'

export const STATES = {
  AK: 'Alaska',
  AL: 'Alabama',
  AR: 'Arkansas',
  AS: 'American Samoa',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MP: 'Northern Mariana Islands',
  MS: 'Mississippi',
  MT: 'Montana',
  NA: 'National',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VI: 'Virgin Islands',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
}

// State names as Select/Autocomplete options. Value and label are both the full
// name — that's what the state-of-formation columns store.
export const STATES_OPTIONS = Object.values(STATES).map((state: string) => ({
  value: state,
  label: state,
}))

export const FEDERAL_TAX_ID_MASK = '00-0000000'
export const FEDERAL_TAX_VALIDATE_REGEXP = /\d{2}[-]?\d{7}/
export const SOCIAL_SECURITY_NUMBER_MASK = '000-00-0000'
export const SOCIAL_SECURITY_VALIDATE_REGEXP = /\d{3}[-]?\d{2}[-]?\d{4}/
export const DOMAIN_REGEXP = /^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^://\n]+)/

export const ACCEPTED_FILES_MAPPING: Record<string, Record<string, string[]>> = {
  excel: {
    'application/vnd.ms-excel': ['.xls', '.xlsx', '.csv', '.xlsm'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xls',
      '.xlsx',
      '.csv',
      '.xlsm',
    ],
    'text/csv': ['.xls', '.xlsx', '.csv', '.xlsm'],
    'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xls', '.xlsx', '.csv', '.xlsm'],
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12': ['.xls', '.xlsx', '.csv', '.xlsm'],
    'application/vnd.ms-excel.template.macroEnabled.12': ['.xls', '.xlsx', '.csv', '.xlsm'],
    'application/vnd.ms-excel.addin.macroEnabled.12': ['.xls', '.xlsx', '.csv', '.xlsm'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template': [
      '.xls',
      '.xlsx',
      '.csv',
      '.xlsm',
    ],
  },
  pdf: {
    'application/pdf': ['.pdf'],
  },
  document: {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
  },
  image: {
    'image/*': ['.jpeg', '.png', '.jpg'],
  },
  html: {
    'text/html': ['.html', '.htm'],
  },
}

export const SAVED_LABEL_DELAY = 3000

/**
 * Debounce for search-as-you-type fields, so a filter box costs one request per
 * pause rather than one per keystroke. Shared so the app's search inputs settle at
 * the same pace instead of each picking a number.
 */
export const SEARCH_DEBOUNCE_MS = 300

export const excelOnlineLink = (fileId: string) =>
  `https://dwightfunding.app.box.com/integrations/officeonline/openOfficeOnline?fileId=${fileId}&sharedAccessCode=`

export const boxOnlineLink = (type: 'file' | 'folder', id: string) =>
  `https://dwightfunding.app.box.com/${type}/${id}`

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- shared empty default consumed by 100+ usages
export const DEFAULT_EMPTY_ARRAY_PROP: any[] = []

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- shared empty default consumed by 100+ usages
export const DEFAULT_EMPTY_OBJECT_PROP: Record<string, any> = {}

// Shown on the Company Overview when the host record resolves to no CRM company.
// The company id gates inline editing on the At a glance card AND the People /
// Organizations rails (both self-hide without it), so the surface would otherwise
// degrade to read-only with no explanation — which reads as "editing is broken".
export const NO_CRM_COMPANY_MESSAGE =
  'No linked CRM company — company details, people and organizations are read-only.'

export const NOTES_FILTER_ICONS: Partial<Record<NotesFilterOptions, IconOrSvg>> = {
  [NotesFilterOptions.All]: 'list',
  [NotesFilterOptions.Pinned]: 'star',
  [NotesFilterOptions.Open]: 'window-open',
  [NotesFilterOptions.Resolved]: 'check-circle',
  [NotesFilterOptions.CallAgenda]: 'phone',
  [NotesFilterOptions.Archived]: 'archive',
  [NotesFilterOptions.ActionRequired]: 'flag-flat',
}
